// /apps/patient/src/pages/progress/components/SessionCard.tsx
import { format, parseISO } from 'date-fns';
import { ChevronRight, Clock, CheckCircle, Activity, AlertCircle } from 'lucide-react';

export interface SessionData {
  id: string;
  session_date: string;
  status: 'completed' | 'partial' | 'in_progress';
  exercises_completed: number;
  total_exercises_prescribed: number;
  duration_minutes?: number | null;
  prescription_name?: string;
  assignment_type?: string;
  started_at?: string;
  completed_at?: string;
}

interface SessionCardProps {
  session: SessionData;
  onClick: () => void;
}

export function SessionCard({ session, onClick }: SessionCardProps) {
  const completionPercentage = session.total_exercises_prescribed > 0
    ? Math.round((session.exercises_completed / session.total_exercises_prescribed) * 100)
    : 0;

  const getStatusBadge = () => {
    switch (session.status) {
      case 'completed':
        return (
          <span className="flex items-center text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </span>
        );
      case 'partial':
        return (
          <span className="flex items-center text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full text-xs">
            <AlertCircle className="h-3 w-3 mr-1" />
            Partial
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'EEEE, MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-lg border p-4 hover:bg-gray-50 transition-colors text-left"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
            <Activity className="h-5 w-5 text-blue-600" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-medium text-gray-900 truncate">
                {session.prescription_name || 'Workout'}
              </h4>
              {getStatusBadge()}
            </div>

            <p className="text-sm text-gray-600">
              {formatDate(session.session_date)}
            </p>

            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                {session.exercises_completed}/{session.total_exercises_prescribed} exercises
              </span>

              {session.duration_minutes && (
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {session.duration_minutes} min
                </span>
              )}
            </div>

            {/* Completion progress bar */}
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    completionPercentage >= 100 ? 'bg-green-500' :
                    completionPercentage >= 50 ? 'bg-blue-500' : 'bg-yellow-500'
                  }`}
                  style={{ width: `${Math.min(completionPercentage, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
      </div>
    </button>
  );
}
